import { isFunction, isString, isUndefined, isCallable, isObject, isDefined, isSymbol } from '#library/assertion.library.js';
import { hasKey, hasOwn } from '#library/primitive.library.js';
import { secureRef, delegate } from '#library/proxy.library.js';
import { cast } from '#library/type.library.js';
import { sym, isTempo } from '../support/support.symbol.js';
import { TempoError } from '../support/support.error.js';
import { getRuntime } from '../support/support.runtime.js';
import { logError } from '#tempo/support/support.util.js';
import { TEMPO_VERSION } from '../tempo.version.js';
/**
 * Resolves the host Tempo class constructor for a given instance, value, or runtime environment.
 *
 * @param t - A Tempo instance, constructor, or any value to resolve the host from
 * @returns The Tempo class constructor
 */
export function getHost(t) {
    const TempoClass = getRuntime().modules['Tempo'];
    if (isCallable(t))
        return t;
    if (isTempo(t))
        return t.constructor ?? TempoClass;
    return TempoClass ?? t?.constructor;
}
/**
 * Verifies that a module is loaded for the specified Tempo host.
 *
 * The `term` alias is resolved to `TermsModule`.
 *
 * @param t - The Tempo instance or constructor to inspect
 * @param module - The module name to verify
 * @param silent - Whether to return `false` instead of reporting a missing module
 * @returns `true` if the module is loaded, `false` if it is missing and silent or catch mode is enabled
 * @throws TempoError if the module is missing and neither silent nor catch mode is enabled
 */
export function ensureModule(t, module, silent = false) {
    const host = getHost(t);
    const rt = getRuntime();
    const mod = module === 'term' ? 'TermsModule' : module;
    const hostLogic = rt.modules[mod];
    const state = host[sym.$Internal]?.() || rt.state;
    // terms fallback only applies when the canonical module entry actually exists in the discovery database
    const isTermsLoaded = (mod === 'TermsModule') && state &&
        (isDefined(hostLogic) || rt.installed.has('TermsModule') || state.pluginsDb.plugins.some((p) => p.name === 'TermsModule')) &&
        state.pluginsDb.terms.length > 0;
    if (!isDefined(hostLogic) && !isTermsLoaded) {
        const baseName = mod.endsWith('Module') ? mod.slice(0, -6) : mod;
        const importPath = baseName === 'Terms' ? 'term' : baseName.toLowerCase();
        const msg = `${mod} not loaded. (Did you forget to Tempo.use(${mod}) or import '#tempo/${importPath}' / '@magmacomputing/tempo/${importPath}'?)`;
        if (!silent)
            logError(msg, t?.config);
        if (silent)
            return false;
        if (t?.config?.catch === true)
            return false;
        throw new TempoError(msg);
    }
    return true;
}
/**
 * ## interpret
 * Utility to safely delegate calls to the Tempo Interpreter with catch-support.
 *
 * @param t - The Tempo instance to operate on
 * @param module - The module name to invoke
 * @param methodOrFallback - Optional method name within the module, or fallback function
 * @param silent - If true, suppresses errors and returns fallback values (default: false)
 * @param args - Arguments to pass to the module or method
 * @returns The result of the module invocation, or fallback value on error
 */
export function interpret(t, module, methodOrFallback, silent = false, ...args) {
    const host = getHost(t);
    // 1. Module Validation
    if (!ensureModule(t, module, silent)) {
        if (isFunction(methodOrFallback))
            return methodOrFallback.apply(t, args);
        if ((isString(methodOrFallback) || isUndefined(methodOrFallback)) && (t?.config?.catch === true || silent))
            return t;
        return undefined;
    }
    const rt = getRuntime();
    const hostLogic = rt.modules[module];
    // 2. Resolve the specific logic (either the module itself or a sub-method)
    const logic = isString(methodOrFallback) ? hostLogic[methodOrFallback] : hostLogic;
    // 3. Logic Not Found or Not a Function
    if (!isFunction(logic)) {
        // Fallback to calling the function if provided
        if (isFunction(methodOrFallback))
            return methodOrFallback.apply(t, args);
        // Special case: if hostLogic is an object and the first arg is a valid method name
        if (isString(args[0]) && hasKey(hostLogic, args[0], isFunction)) {
            const method = args.shift();
            return hostLogic[method].apply(t, args);
        }
        const msg = `${module} method '${String(methodOrFallback)}' not found`;
        logError(msg, t?.config);
        throw new TempoError(msg);
    }
    // 4. Execute the logic
    return logic.apply(t, args);
}
/**
 * ## defineModule
 * Used to register an internal modularization component.
 *
 * @param module - The module plugin definition to register
 * @returns The registered module with plugin type metadata attached
 */
export function defineModule(module) {
    const result = { ...module, [sym.$PluginType]: 'module' };
    registerPlugin(result);
    return cast(result);
}
/**
 * Safely attaches static properties to a class, ensuring they are non-enumerable
 * to prevent @Immutable from freezing them.
 *
 * @param TempoClass - The Tempo class to attach properties to
 * @param props - Record of property names and values or descriptors to attach
 */
export function attachStatics(TempoClass, props) {
    for (const [key, val] of Object.entries(props)) {
        const isDescriptor = isObject(val) && (val[sym.$Descriptor] === true ||
            ((isDefined(val.get) || isDefined(val.set) || isDefined(val.value) || isDefined(val.writable) || isDefined(val.configurable) || isDefined(val.enumerable)) &&
                (!isDefined(val.get) || isFunction(val.get)) &&
                (!isDefined(val.set) || isFunction(val.set))));
        if (hasOwn(TempoClass, key)) {
            const desc = Object.getOwnPropertyDescriptor(TempoClass, key);
            if (desc) {
                if (!isDescriptor && desc.value === val)
                    continue;
                if (isDescriptor) {
                    const isAccessor = hasKey(val, 'get', isFunction) || hasKey(val, 'set', isFunction);
                    if (isAccessor) {
                        const v = val;
                        if (desc.get === v.get && desc.set === v.set)
                            continue;
                    }
                    else if (hasKey(val, 'value')) {
                        const v = val;
                        if (desc.value === v.value &&
                            desc.writable === (v.writable ?? true) &&
                            desc.enumerable === (v.enumerable ?? false) &&
                            desc.configurable === (v.configurable ?? true))
                            continue;
                    }
                }
            }
            const msg = `Static name collision on "${key}". Property is already defined on the host class.`;
            logError(msg, { ...TempoClass?.config, catch: true });
            continue;
        }
        // attachStatics: Intentional ordering in Object.defineProperty overrides any caller-provided flags in isDescriptor to force non-enumerable behavior (avoiding @Immutable exposure).
        Object.defineProperty(TempoClass, key, {
            ...(isDescriptor ? val : { value: val, writable: true }),
            enumerable: false,
            configurable: true
        });
    }
}
/**
 * ## defineInterpreterModule
 * Used to register a module that attaches methods to the Tempo sym.$Interpreter registry.
 *
 * @param name - The module name to register in the interpreter
 * @param logic - The module logic or method collection to install
 * @param statics - Optional static properties to attach to the Tempo class
 * @returns The registered module plugin definition
 */
export function defineInterpreterModule(name, logic, statics) {
    return defineModule({
        name,
        version: TEMPO_VERSION,
        install(TempoClass) {
            const rt = getRuntime();
            const modules = rt.modules;
            // 1. Secure the Global Registry
            if (isUndefined(modules[name])) {
                modules[name] = logic;
            }
            else if (modules[name] !== logic) {
                throw new TempoError(`Tempo Security: Core Module clash for '${name}'. Logic is already defined.`);
            }
            // 2. Fallback for legacy class-local access
            if (isUndefined(TempoClass[sym.$Interpreter])) {
                Object.defineProperty(TempoClass, sym.$Interpreter, {
                    value: secureRef({}),
                    enumerable: false,
                    configurable: true,
                    writable: true
                });
            }
            if (isDefined(TempoClass[sym.$Interpreter][name])) {
                if (TempoClass[sym.$Interpreter][name] !== logic)
                    throw new TempoError(`Tempo Interpreter Module clash: '${name}' logic is already defined.`);
            }
            else {
                TempoClass[sym.$Interpreter][name] = logic;
            }
            // 3. Attach static methods if provided
            if (isDefined(statics))
                attachStatics(TempoClass, statics);
        },
    });
}
/**
 * ## definePlugin
 * Used to register a plugin.
 *
 * @param plugin - The plugin definition to register
 * @returns The registered plugin factory with plugin type metadata attached
 */
export function definePlugin(plugin) {
    const factory = function (options) {
        return {
            ...plugin,
            options,
            [sym.$PluginType]: 'plugin'
        };
    };
    const { name, ...rest } = plugin;
    const result = Object.assign(factory, rest, { [sym.$PluginType]: 'plugin' });
    if (name) {
        Object.defineProperty(result, 'name', { value: name, configurable: true });
    }
    registerPlugin(result);
    return cast(result);
}
/**
 * Registration hook for general plugins. Validates and persists the plugin
 * to the discovery database. Sandbox registrations persist in sandbox state
 * without calling rt.addExtension(plugin), while non-sandbox plugins are
 * persisted to the global extension list.
 *
 * @param plugin - The plugin to register
 * @param state - Optional state context for sandboxed registrations
 * @returns The registered plugin
 */
export function registerPlugin(plugin, state) {
    const rt = getRuntime();
    // Validate and persist in the state's discovery database.
    if (state)
        rt.addPlugin(state, plugin);
    else if (rt.state)
        rt.addPlugin(rt.state, plugin);
    // Only persist to the global extension list for global-state registrations.
    // Sandbox registrations (state !== rt.state) are tracked in the sandbox's
    // ScopedSet and must not bleed into the global rt.extensions array.
    const targetState = state ?? rt.state;
    if (!targetState || targetState === rt.state) {
        rt.addExtension(plugin);
    }
    rt.emit(sym.$Register, plugin);
    return plugin;
}
/**
 * ## defineNamespace
 * Creates a lazy-loaded property namespace on the Tempo instance.
 *
 * @param config - The namespace configuration including name, version, and resolvers
 * @returns A registered namespace plugin factory
 */
export function defineNamespace(config) {
    if (isSymbol(config.name) && !config.name.description)
        throw new TempoError('Tempo Security: Symbol namespaces must have a description.');
    const namespaceStr = isSymbol(config.name)
        ? (Symbol.keyFor(config.name) ? `@@${Symbol.keyFor(config.name)}` : `@${config.name.description}`)
        : config.name;
    const pluginName = `${namespaceStr}Namespace`;
    const weakCache = new WeakMap();
    const plugin = cast({
        name: pluginName,
        version: config.version ?? TEMPO_VERSION,
        [sym.$PluginType]: 'namespace',
        install(TempoClass, options) {
            Object.defineProperty(TempoClass.prototype, config.name, {
                get() {
                    const cacheKey = isSymbol(config.name) ? namespaceStr : `_${namespaceStr}`;
                    const isExtensible = Reflect.isExtensible(this);
                    if (!isExtensible && weakCache.has(this))
                        return weakCache.get(this);
                    if (isExtensible && this[cacheKey])
                        return this[cacheKey];
                    const target = Object.create(null);
                    const proxy = delegate(target, (key) => {
                        const resolver = config.resolvers[key];
                        if (resolver)
                            return resolver(this);
                        return undefined;
                    }, true);
                    if (isExtensible) {
                        Object.defineProperty(this, cacheKey, {
                            value: proxy,
                            writable: true,
                            configurable: true,
                            enumerable: false
                        });
                    }
                    else {
                        weakCache.set(this, proxy);
                    }
                    return proxy;
                },
                configurable: true,
                enumerable: false
            });
        }
    });
    const factory = function (options) {
        return {
            ...plugin,
            options,
            [sym.$PluginType]: 'namespace'
        };
    };
    const { name, ...rest } = plugin;
    const result = Object.assign(factory, rest, { [sym.$PluginType]: 'namespace' });
    if (name) {
        Object.defineProperty(result, 'name', { value: name, configurable: true });
    }
    registerPlugin(result);
    return cast(result);
}
/**
 * Automatically registers a plugin onto the active Tempo constructor or global ambient instance.
 * Intended for use in plugin `/install` side-effect subpath entry points.
 *
 * @param plugin - The plugin definition or factory to register
 */
export function autoInstall(plugin) {
    const host = getHost(null);
    const globalTempo = globalThis.Tempo;
    if (hasKey(host, 'use', isFunction)) {
        host.use(plugin);
    }
    else if (hasKey(globalTempo, 'use', isFunction)) {
        globalTempo.use(plugin);
    }
    else {
        const rt = getRuntime();
        if (!Array.isArray(rt.modules['$pendingAutoInstalls']))
            rt.modules['$pendingAutoInstalls'] = [];
        rt.modules['$pendingAutoInstalls'].push(plugin);
    }
}
